import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  FileText,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Building,
  MapPin,
  Briefcase,
  Download
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

const JobApplications = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');

  useEffect(() => {
    fetchJob();
    fetchApplications();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const response = await api.get(`/api/jobs/${jobId}/`);
      setJob(response.data);
    } catch (error) {
      toast.error('Failed to fetch job details');
      console.error('Error fetching job:', error);
      navigate('/company-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    setApplicationsLoading(true);
    try {
      const response = await api.get(`/api/applications/?job=${jobId}`);
      const applicationsData = Array.isArray(response.data) ? response.data : response.data?.results || [];
      setApplications(applicationsData);
    } catch (error) {
      toast.error('Failed to fetch applications');
      console.error('Error fetching applications:', error);
      setApplications([]);
    } finally {
      setApplicationsLoading(false);
    }
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      await api.patch(`/api/applications/${applicationId}/`, {
        status: newStatus,
        reviewed_at: new Date().toISOString()
      });
      
      // Update local state
      setApplications(prev => 
        prev.map(app => 
          app.id === applicationId 
            ? { ...app, status: newStatus, reviewed_at: new Date().toISOString() }
            : app
        )
      );
      
      toast.success('Application status updated successfully!');
    } catch (error) {
      toast.error('Failed to update application status');
      console.error('Error updating status:', error);
    }
  };

  const downloadCSV = () => {
    const csvData = [
      ['Name', 'Email', 'Applied Date', 'Status', 'Expected Salary', 'Available From', 'Cover Letter'],
      ...filteredApplications.map(app => [
        app.candidate_name || '',
        app.candidate_email || '',
        new Date(app.applied_at).toLocaleDateString(),
        app.status || '',
        app.expected_salary || '',
        app.available_from ? new Date(app.available_from).toLocaleDateString() : '',
        app.cover_letter ? app.cover_letter.replace(/\n/g, ' ').substring(0, 100) + '...' : ''
      ])
    ];

    const csvContent = csvData.map(row => row.map(field => `"${field}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `job-applications-${job.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Applications exported successfully!');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'reviewed':
        return 'bg-blue-100 text-blue-800';
      case 'shortlisted':
        return 'bg-green-100 text-green-800';
      case 'interviewed':
        return 'bg-purple-100 text-purple-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'reviewed':
        return <Eye className="w-4 h-4" />;
      case 'shortlisted':
        return <CheckCircle className="w-4 h-4" />;
      case 'interviewed':
        return <User className="w-4 h-4" />;
      case 'accepted':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const filteredApplications = selectedStatus === 'all' 
    ? applications 
    : applications.filter(app => app.status === selectedStatus);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Job not found</h3>
          <p className="mt-1 text-sm text-gray-500">The job you're looking for doesn't exist or has been removed.</p>
          <div className="mt-6">
            <Link
              to="/company-dashboard"
              className="btn-primary"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/company-dashboard')}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Job Applications</h1>
                  <p className="text-sm text-gray-600 mt-1">
                    {job.title} at {job.company_name}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-500">
                  {applications.length} application{applications.length !== 1 ? 's' : ''}
                </div>
                {applications.length > 0 && (
                  <button
                    onClick={downloadCSV}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export CSV</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Job Info Card */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{job.title}</h2>
              <div className="flex items-center space-x-4 text-gray-600 text-sm">
                <div className="flex items-center">
                  <Building className="w-4 h-4 mr-1" />
                  {job.company_name}
                </div>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />
                  {job.location_name}
                </div>
                <div className="flex items-center">
                  <Briefcase className="w-4 h-4 mr-1" />
                  {job.job_type_name}
                </div>
              </div>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              {job.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Filter and Stats */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Filter by status:</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Applications</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="interviewed">Interviewed</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-6 text-sm">
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1 text-yellow-500" />
                <span className="text-gray-600">
                  {applications.filter(app => app.status === 'pending').length} Pending
                </span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-1 text-green-500" />
                <span className="text-gray-600">
                  {applications.filter(app => app.status === 'shortlisted').length} Shortlisted
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Applications List */}
        <div className="space-y-4">
          {applicationsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No applications found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {selectedStatus === 'all' 
                  ? 'No applications have been submitted for this job yet.'
                  : `No applications with status "${selectedStatus}".`}
              </p>
            </div>
          ) : (
            filteredApplications.map((application) => {
              console.log('Application data:', application);
              console.log('Resume data:', application.resume);
              return (
              <div key={application.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                          <User className="w-6 h-6 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {application.candidate_name}
                          </h3>
                          <p className="text-sm text-gray-600">{application.candidate_email}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="w-4 h-4 mr-2" />
                          Applied {new Date(application.applied_at).toLocaleDateString()}
                        </div>
                        {application.expected_salary && (
                          <div className="flex items-center text-sm text-gray-600">
                            <span className="font-medium">Expected Salary:</span>
                            <span className="ml-1">${application.expected_salary.toLocaleString()}</span>
                          </div>
                        )}
                        {application.available_from && (
                          <div className="flex items-center text-sm text-gray-600">
                            <span className="font-medium">Available from:</span>
                            <span className="ml-1">{new Date(application.available_from).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>

                      {application.cover_letter && (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-gray-900 mb-1 text-left">Cover Letter:</h4>
                          <p className="text-sm text-gray-700 line-clamp-3 text-left">
                            {application.cover_letter}
                          </p>
                        </div>
                      )}

                      {/* {application.resume && (
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-gray-900 mb-1 text-left">Resume:</h4>
                          <div className="flex items-center space-x-2">
                            <FileText className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-600">{application.resume.name}</span>
                          </div>
                        </div>
                      )} */}
                    </div>

                    <div className="ml-6 flex flex-col items-end space-y-3">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                          {getStatusIcon(application.status)}
                          <span className="ml-1">{application.status?.charAt(0).toUpperCase() + application.status?.slice(1) || 'Unknown'}</span>
                        </span>
                      </div>

                      <div className="flex items-center space-x-2">
                        <select
                          value={application.status}
                          onChange={(e) => handleStatusUpdate(application.id, e.target.value)}
                          className="text-xs px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="pending">Pending</option>
                          <option value="reviewed">Reviewed</option>
                          <option value="shortlisted">Shortlisted</option>
                          <option value="interviewed">Interviewed</option>
                          <option value="accepted">Accepted</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>

                      <div className="flex items-center space-x-2">
                        {application.resume && application.resume.file ? (
                          <a
                            href={application.resume.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded hover:bg-primary-100 transition-colors"
                            title="Download Resume"
                          >
                            <Download className="w-3 h-3" />
                            <span>Download Resume</span>
                          </a>
                        ) : (
                          <div className="flex items-center space-x-1 px-3 py-1 text-xs font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded cursor-not-allowed">
                            <Download className="w-3 h-3" />
                            <span>No Resume</span>
                          </div>
                        )}
                      </div>

                      {application.reviewed_at && (
                        <div className="text-xs text-gray-500">
                          Reviewed {new Date(application.reviewed_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default JobApplications;
